/**
 * Workspace Page
 * Three-column layout: Resources (left), Chat (center), Studio (right)
 * Project-level workspace for AI-powered marketing content generation.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ProjectHeader } from "@/components/layout/project-header";
import { SocialPlatformModal } from "@/components/social-platform-modal";
import { ResizableResourcesPanel } from "@/components/workspace/resizable-resources-panel";
import { UploadCreativesModal } from "@/components/workspace/upload-creatives-modal";
import { useFilePreview } from "@/components/workspace/use-file-preview";
import { useResourceActions } from "@/components/workspace/use-resource-actions";
import { useResourceSelection } from "@/components/workspace/use-resource-selection";
import { useStudioTools } from "@/components/workspace/use-studio-tools";
import { useWorkspaceChat } from "@/components/workspace/use-workspace-chat";
import { WorkspaceBriefModal } from "@/components/workspace/workspace-brief-modal";
import { WorkspaceChat } from "@/components/workspace/workspace-chat";
import { WorkspaceFilePreviewModal } from "@/components/workspace/workspace-file-preview-modal";
import { WorkspaceResourceList } from "@/components/workspace/workspace-resource-list";
import { WorkspaceResourcesHeader } from "@/components/workspace/workspace-resources-header";
import { WorkspaceStudioPanel } from "@/components/workspace/workspace-studio-panel";
import { isDeletionQuestion, useAgentQuestions } from "@/hooks/use-agent-questions";
import { useDeletionAnswer } from "@/hooks/use-deletion-answer";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import {
	api,
	type ChatHistoryMessage,
	type Project,
	type ProjectBrief,
	type ProjectResources,
	type ToolResult,
} from "@/lib/api";

// Stable fallbacks: a fresh `[]` each render would re-fire every effect that
// depends on these lists (e.g. chat auto-scroll) while data loads.
const NO_MESSAGES: ChatHistoryMessage[] = [];
const NO_TOOL_RESULTS: ToolResult[] = [];

export function WorkspacePage() {
	const { user } = useAuth();
	const { activeTeam } = useActiveTeam();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const monitoringReviewId = searchParams.get("review") || undefined;
	const monitoringNotificationId =
		searchParams.get("notification") || undefined;
	const { projectId } = useParams<{ projectId: string }>();

	const [briefModalOpen, setBriefModalOpen] = useState(false);
	const [creativesModalOpen, setCreativesModalOpen] = useState(false);

	// API queries
	const { data: projectDetails } = useQuery<Project>({
		queryKey: ["projectDetails", projectId],
		queryFn: () => api.getProjectDetails(projectId!),
		enabled: !!projectId,
	});

	const { data: resourceData, isLoading: isLoadingFiles } =
		useQuery<ProjectResources>({
			queryKey: ["projectFiles", projectId],
			queryFn: () => api.getProjectFiles(projectId!),
			enabled: !!projectId,
		});

	const creatives = resourceData?.creatives ?? [];

	const { data: chatMessages = NO_MESSAGES, isLoading: isLoadingChat } = useQuery<
		ChatHistoryMessage[]
	>({
		queryKey: ["chatHistory", projectId],
		queryFn: () => api.getChatHistory(projectId!),
		enabled: !!projectId,
	});

	const { data: toolResults = NO_TOOL_RESULTS, isLoading: isLoadingTools } = useQuery<
		ToolResult[]
	>({
		queryKey: ["toolResults", projectId],
		queryFn: () => api.getToolResults(projectId!),
		enabled: !!projectId,
	});

	const { pendingQuestion, clearQuestion, resync } = useAgentQuestions(projectId);
	const deletion = useDeletionAnswer({ projectId, clearQuestion, resyncQuestion: resync });
	const pendingDeletion = isDeletionQuestion(pendingQuestion) ? pendingQuestion : null;

	const { data: briefData, isLoading: isLoadingBrief } = useQuery<ProjectBrief>(
		{
			queryKey: ["projectBrief", projectId],
			queryFn: () => api.getProjectBrief(projectId!),
			enabled: !!projectId,
			retry: false,
		},
	);

	// Section state
	const selection = useResourceSelection(resourceData, briefData);
	const chat = useWorkspaceChat({
		projectId,
		teamId: activeTeam?.id,
		monitoringReviewId,
		monitoringNotificationId,
		clearQuestion,
		resyncQuestion: resync,
		getSelectedFileIds: selection.getSelectedFileIds,
	});
	const { uploadMutation, presentationMutation, handleFileSelect } =
		useResourceActions({ projectId, userId: user?.id, activeTeam });
	const preview = useFilePreview(projectId);
	const studio = useStudioTools(projectId);

	return (
		<div className="flex flex-col h-screen overflow-hidden bg-bg-canvas">
			{/* ═══════════════════ TOP PROJECT HEADER ═══════════════════ */}
			<ProjectHeader projectTitle={projectDetails?.title || "Workspace"} />

			<div className="flex flex-1 gap-0 overflow-hidden">
				<ResizableResourcesPanel>
					<WorkspaceResourcesHeader
						isUploading={uploadMutation.isPending}
						uploadDisabled={uploadMutation.isPending || !user?.id || !projectId}
						onFileChange={handleFileSelect}
						onOpenCreatives={() => setCreativesModalOpen(true)}
						isGeneratingPresentation={presentationMutation.isPending}
						presentationDisabled={presentationMutation.isPending || !projectId}
						onGeneratePresentation={() => presentationMutation.mutate()}
					/>
					<WorkspaceResourceList
						isLoading={isLoadingFiles || isLoadingBrief}
						resources={selection.resources}
						creatives={creatives}
						hasBrief={!!briefData}
						allSelected={selection.allSelected}
						briefSelected={selection.briefSelected}
						onToggleSelectAll={selection.toggleSelectAll}
						onToggleResource={selection.toggleResource}
						onToggleBrief={selection.toggleBrief}
						onOpenBrief={() => setBriefModalOpen(true)}
						onPreviewFile={preview.openPreview}
					/>
				</ResizableResourcesPanel>

				<WorkspaceFilePreviewModal
					open={preview.open}
					onClose={preview.close}
					loading={preview.loading}
					error={preview.error}
					url={preview.url}
					kind={preview.kind}
				/>

				<WorkspaceChat
					messages={chatMessages}
					isLoading={isLoadingChat}
					isSending={chat.isSending}
					questionOptions={pendingQuestion?.options}
					onOptionSelect={chat.handleAgentOptionSelect}
					pendingDeletion={pendingDeletion}
					onDeletionChoice={(choice) => deletion.answer(pendingDeletion?.questionId, choice)}
					isAnsweringDeletion={deletion.isAnswering}
					showMonitoringNotice={!!monitoringReviewId}
					monitoringReviewId={monitoringReviewId}
					onDismissMonitoring={() =>
						setSearchParams((params) => {
							params.delete("review");
							params.delete("notification");
							return params;
						})
					}
					input={chat.chatInput}
					onInputChange={chat.setChatInput}
					onSubmit={chat.handleSendMessage}
				/>

				<WorkspaceStudioPanel
					loadingTool={studio.loadingTool}
					onRunTool={studio.handleToolClick}
					onOpenContentPlan={studio.openContentPlan}
					toolResults={toolResults}
					isLoadingResults={isLoadingTools}
					onOpenResult={() => navigate(`/canvas/${projectId}`)}
				/>
			</div>

			<UploadCreativesModal
				open={creativesModalOpen}
				onClose={() => setCreativesModalOpen(false)}
			/>

			<WorkspaceBriefModal
				open={briefModalOpen}
				brief={briefData}
				onClose={() => setBriefModalOpen(false)}
			/>

			<SocialPlatformModal
				open={studio.socialModalOpen}
				onClose={() => studio.setSocialModalOpen(false)}
				selected={studio.selectedSocials[0] ?? null}
				onSelect={(platformId) =>
					studio.setSelectedSocials(platformId ? [platformId] : [])
				}
				onContinue={studio.runContentPlan}
			/>
		</div>
	);
}
